import { useQueryClient } from '@tanstack/react-query';
import { getTransactionNotes } from '@/lib/demo-data/transaction-notes';
import type {
  BulkDeleteTransactionNotesApiResponse,
  BulkTransactionNotesApiResponse,
  CreateBulkTransactionNotesRequest,
  CreateTransactionNoteRequest,
  DeleteBulkTransactionNotesRequest,
  DeleteTransactionNoteRequest,
  TransactionNote,
  TransactionNoteApiItem,
  TransactionNotesApiResponse,
  UpdateTransactionNoteRequest,
} from '@/types/transaction-note';
import {
  addTransactionNoteDemoResponse,
  deleteBulkTransactionNotesDemoResponse,
  updateTransactionNoteDemoResponse,
} from '@/services/demo/transaction-notes-demo.service';
import { useApi } from './useApi';
import { useModeAwareMutation, useModeAwareQuery } from './useModeAwareQuery';

function mapApiItemToTransactionNote(item: TransactionNoteApiItem): TransactionNote {
  return {
    id: String(item.id),
    transactionId: String(item.transactionId),
    content: item.content,
    createdAt: item.createdAt,
    createdBy: {
      id: item.createdByUserId === null ? '' : String(item.createdByUserId),
      name: item.createdByUserName,
    },
  };
}

/**
 * Hook to fetch transaction notes
 * Handles both Demo and Live modes
 */
export function useTransactionNotes(transactionId: number | undefined) {
  const { apiGet } = useApi();
  const mockNotes = transactionId ? getTransactionNotes(String(transactionId)) : [];

  return useModeAwareQuery<TransactionNote[], Error>(
    ['transaction-notes', transactionId],
    async ({ signal }) => {
      if (!transactionId) return [];
      const response = await apiGet<TransactionNotesApiResponse>(
        `/api/v1/transactions/${transactionId}/notes`,
        undefined,
        signal,
      );
      return (response.notes || []).map(mapApiItemToTransactionNote);
    },
    mockNotes,
    {
      enabled: !!transactionId,
      staleTime: 0,
      refetchOnMount: 'always',
    },
  );
}

/**
 * Hook to add a new transaction note
 * Handles both Demo and Live modes
 */
export function useAddTransactionNote() {
  const { apiPost } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<
    TransactionNote,
    Error,
    CreateTransactionNoteRequest & { userId: string; userName: string }
  >(
    async (request) => {
      const response = await apiPost<TransactionNoteApiItem>(`/api/v1/transactions/${request.transactionId}/notes`, {
        content: request.content,
      });
      return mapApiItemToTransactionNote(response);
    },
    addTransactionNoteDemoResponse,
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries({ queryKey: ['transaction-notes', variables.transactionId] });
        queryClient.invalidateQueries({ queryKey: ['transaction-recent-activities', variables.transactionId] });
      },
    },
  );
}

/**
 * Hook to add notes to multiple transactions in bulk
 * Handles both Demo and Live modes
 */
export function useAddBulkTransactionNotes() {
  const { apiPost } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<
    BulkTransactionNotesApiResponse,
    Error,
    CreateBulkTransactionNotesRequest & { userId: string; userName: string }
  >(
    async (request) => {
      const response = await apiPost<BulkTransactionNotesApiResponse>('/api/v1/transactions/notes/bulk', {
        transactionIds: request.transactionIds,
        content: request.content,
      });
      return response;
    },
    async (request) => {
      return {
        createdCount: request.transactionIds.length,
        failedTransactionIds: [],
      };
    },
    {
      onSuccess: (_, variables) => {
        for (const transactionId of variables.transactionIds) {
          queryClient.invalidateQueries({ queryKey: ['transaction-notes', transactionId] });
          queryClient.invalidateQueries({ queryKey: ['transaction-recent-activities', transactionId] });
        }
      },
    },
  );
}

/**
 * Hook to edit an existing transaction note
 * Handles both Demo and Live modes
 */
export function useUpdateTransactionNote() {
  const { apiPut } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<TransactionNote, Error, UpdateTransactionNoteRequest>(
    async (request) => {
      const response = await apiPut<TransactionNoteApiItem>(
        `/api/v1/transactions/${request.transactionId}/notes/${request.noteId}`,
        { content: request.content },
      );
      return mapApiItemToTransactionNote(response);
    },
    updateTransactionNoteDemoResponse,
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries({ queryKey: ['transaction-notes', variables.transactionId] });
        queryClient.invalidateQueries({ queryKey: ['transaction-recent-activities', variables.transactionId] });
      },
    },
  );
}

/**
 * Hook to delete a transaction note
 * Handles both Demo and Live modes
 */
export function useDeleteTransactionNote() {
  const { apiDelete } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<void, Error, DeleteTransactionNoteRequest>(
    async (request) => {
      await apiDelete<void>(`/api/v1/transactions/${request.transactionId}/notes/${request.noteId}`);
    },
    () => undefined,
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries({ queryKey: ['transaction-notes', variables.transactionId] });
        queryClient.invalidateQueries({ queryKey: ['transaction-recent-activities', variables.transactionId] });
      },
    },
  );
}

/**
 * Hook to delete notes across multiple transactions in bulk
 * Handles both Demo and Live modes
 */
export function useDeleteBulkTransactionNotes() {
  const { apiPost } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<BulkDeleteTransactionNotesApiResponse, Error, DeleteBulkTransactionNotesRequest>(
    async (request) => {
      return apiPost<BulkDeleteTransactionNotesApiResponse>('/api/v1/transactions/notes/bulk-delete', {
        noteIds: request.noteIds,
      });
    },
    deleteBulkTransactionNotesDemoResponse,
    {
      onSuccess: (_, variables) => {
        for (const transactionId of variables.transactionIds) {
          queryClient.invalidateQueries({ queryKey: ['transaction-notes', transactionId] });
          queryClient.invalidateQueries({ queryKey: ['transaction-recent-activities', transactionId] });
        }
      },
    },
  );
}
